import React from 'react';
import { SHELL_WHITE, IRIS_PURPLE, LIVING_CORAL } from '../../../theme/colors';
import { HEALTH_MODES } from './healthProfileModes';
import { useScrollStage } from '../../../hooks/useScrollStage';

// Mobile-only: glass chips directly on the photo (no card background) so the
// image keeps carrying the visual weight, per direction chosen over the
// earlier opaque-card mobile exploration.
// On mobile (progress prop present) chips stagger-enter unselected, then
// Period syncing + PMOS/PCOS animate to selected; desktop equivalent
// (HealthProfileCard.js) is a separate file and always reads HEALTH_MODES'
// `active` field directly, unaffected by this animation.

const ChipsShell = ({ children }) => (
  <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
    <span style={{ fontFamily: 'DM Sans', fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: SHELL_WHITE, opacity: 0.7, textShadow: '0 1px 4px rgba(0,0,0,0.35)' }}>
      Your modes
    </span>
    <span style={{ display: 'block', width: '100%', fontFamily: 'DM Sans', fontSize: 14, lineHeight: 1.45, color: SHELL_WHITE, opacity: 0.9, marginTop: 5, textShadow: '0 1px 4px rgba(0,0,0,0.35)' }}>
      Tap a mode on and Vesna reshapes its coaching around it.
    </span>

    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginTop: 14 }}>{children}</div>
  </div>
);

const StaticHealthProfileChips = () => (
  <ChipsShell>
    {HEALTH_MODES.map(({ label, Icon, active }) => (
      <div
        key={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '9px 14px',
          borderRadius: 999,
          background: active ? `linear-gradient(135deg, ${IRIS_PURPLE}, ${LIVING_CORAL})` : 'rgba(255,255,255,0.14)',
          border: active ? 'none' : '1.5px solid rgba(255,255,255,0.38)',
          backdropFilter: active ? 'none' : 'blur(8px)',
          boxShadow: active ? '0 4px 14px rgba(0,0,0,0.25)' : 'none',
        }}
      >
        <Icon size={14} color={SHELL_WHITE} strokeWidth={2} style={{ opacity: active ? 1 : 0.85 }} />
        <span style={{ fontFamily: 'DM Sans', fontSize: 13, fontWeight: active ? 600 : 500, color: SHELL_WHITE, opacity: active ? 1 : 0.85 }}>
          {label}
        </span>
      </div>
    ))}
  </ChipsShell>
);

// Beats (progress 0-1 across the card's pinned scroll runway):
// 0.00-0.15 all 6 chips stagger-enter unselected
// 0.15-0.35 hold
// 0.35-0.55 "Period syncing" snaps selected
// 0.55-0.75 "PMOS/PCOS" snaps selected
// 0.75-1.00 hold completed
// Other 4 chips stay unselected throughout, regardless of HEALTH_MODES' own
// `active` field (that field only drives desktop's separate HealthProfileCard.js).
const AnimatedHealthProfileChips = ({ progress }) => {
  const entered = useScrollStage(progress, 0.02);
  const periodSelected = useScrollStage(progress, 0.4);
  const pmosSelected = useScrollStage(progress, 0.6);

  return (
    <ChipsShell>
      {HEALTH_MODES.map(({ label, Icon }, i) => {
        const active = label === 'Period syncing' ? periodSelected : label === 'PMOS/PCOS' ? pmosSelected : false;
        return (
          <div
            key={label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 14px',
              borderRadius: 999,
              background: active ? `linear-gradient(135deg, ${IRIS_PURPLE}, ${LIVING_CORAL})` : 'rgba(255,255,255,0.14)',
              border: active ? 'none' : '1.5px solid rgba(255,255,255,0.38)',
              backdropFilter: active ? 'none' : 'blur(8px)',
              boxShadow: active ? '0 4px 14px rgba(0,0,0,0.25)' : 'none',
              opacity: entered ? 1 : 0,
              transform: entered ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.3s ease ${i * 0.04}s, transform 0.3s ease ${i * 0.04}s, background 0.25s ease, border 0.25s ease, box-shadow 0.25s ease`,
            }}
          >
            <Icon size={14} color={SHELL_WHITE} strokeWidth={2} style={{ opacity: active ? 1 : 0.85, transition: 'opacity 0.25s ease' }} />
            <span style={{ fontFamily: 'DM Sans', fontSize: 13, fontWeight: active ? 600 : 500, color: SHELL_WHITE, opacity: active ? 1 : 0.85, transition: 'opacity 0.25s ease' }}>
              {label}
            </span>
          </div>
        );
      })}
    </ChipsShell>
  );
};

const HealthProfileChips = ({ progress }) => {
  if (!progress) return <StaticHealthProfileChips />;
  return <AnimatedHealthProfileChips progress={progress} />;
};

export default HealthProfileChips;
